import type { ReactNode } from "react";
import { Link } from "wouter";

export function AnswerFirstAuthorBlock() {
  return (
    <section id="author" className="border-t border-border py-10" aria-label="Author and reviewer">
      <p className="text-sm leading-relaxed text-muted-foreground">
        Written and reviewed by{" "}
        <Link href="/modi-elnadi/" className="font-medium text-foreground underline underline-offset-4">
          Modi Elnadi
        </Link>{" "}
        ·{" "}
        <a
          href="https://www.linkedin.com/in/modielnadi/"
          target="_blank"
          rel="me noopener noreferrer"
          className="font-medium text-foreground underline underline-offset-4"
        >
          LinkedIn
        </a>
      </p>
      <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">
        Founder of Integrated.Social: a design background, an MBA and 20+ years in digital marketing, with £20m+ in paid media managed. Agents draft; a named senior person signs off.
      </p>
    </section>
  );
}

type AnswerFirstSectionsProps = {
  methodTitle: string;
  method: ReactNode;
  fit: ReactNode;
  evidence: ReactNode;
};

/**
 * Shared answer-first sequence. Keep it immediately below a page's opening so
 * the reader sees the method, fit, evidence, reviewer and next step in order.
 */
export function AnswerFirstSections({ methodTitle, method, fit, evidence }: AnswerFirstSectionsProps) {
  return (
    <div className="border-y border-border bg-accent/20">
      <div className="container max-w-5xl py-12">
        <section aria-labelledby="answer-first-method-heading">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#FF8C00]">Method</p>
          <h2 id="answer-first-method-heading" className="mt-3 font-serif text-3xl md:text-4xl">{methodTitle}</h2>
          <div className="mt-4 max-w-3xl text-muted-foreground leading-relaxed">{method}</div>
        </section>
        <section className="mt-10" aria-labelledby="answer-first-fit-heading">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#FF8C00]">Fit</p>
          <h3 id="answer-first-fit-heading" className="mt-3 font-serif text-2xl md:text-3xl">Who this is for, and who it is not for</h3>
          <div className="mt-4 max-w-3xl text-muted-foreground leading-relaxed">{fit}</div>
        </section>
        <section className="mt-10" aria-labelledby="answer-first-evidence-heading">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#FF8C00]">Evidence and limits</p>
          <h3 id="answer-first-evidence-heading" className="mt-3 font-serif text-2xl md:text-3xl">What supports the work</h3>
          <div className="mt-4 max-w-3xl text-muted-foreground leading-relaxed">{evidence}</div>
        </section>
        <AnswerFirstAuthorBlock />
        <section className="pt-2" aria-labelledby="answer-first-next-step-heading">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#FF8C00]">Next step</p>
          <h3 id="answer-first-next-step-heading" className="mt-3 font-serif text-2xl md:text-3xl">Choose the route that gives you useful context</h3>
          <p className="mt-4 max-w-3xl text-muted-foreground leading-relaxed">Use the Free AI Visibility Score for a high-level baseline, or book a 30-minute call when a senior conversation about scope is more useful.</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link href="/free-ai-growth-audit/" className="inline-flex min-h-11 items-center justify-center rounded-lg bg-[#FF8C00] px-5 py-3 text-sm font-bold text-[#101722] transition hover:bg-[#ffad4d]">Check my AI visibility</Link>
            <a href="https://calendly.com/modielnadi/30min" target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center justify-center rounded-lg border border-current px-5 py-3 text-sm font-semibold transition hover:border-[#FF8C00] hover:text-[#FF8C00]">Book a 30-minute call</a>
          </div>
        </section>
      </div>
    </div>
  );
}
